import type { OrderType } from '@features/products/catalog';
import type { ProductCountBuckets, ProductCounts } from '@features/products/counts/productCounts';

import type { HomeCounts, RooftopCounts } from '../homeCounts';

/**
 * On Home, Guest Rides show only in Active Orders, so their "upcoming" figures fold into active and
 * the hero matches the feed. This re-buckets only the home hero's copy; the Rides tab keeps its
 * upcoming bucket.
 */
export function foldGuestRideUpcoming(byProduct: Partial<Record<OrderType, ProductCounts>>): Partial<Record<OrderType, ProductCounts>> {
  const guest = byProduct['guest-rides'];
  if (!guest) return byProduct;
  const fold = (b: ProductCountBuckets): ProductCountBuckets => ({ ...b, active: b.active + b.upcoming, upcoming: 0 });
  return {
    ...byProduct,
    'guest-rides': {
      ...guest,
      buckets: fold(guest.buckets),
      byCustomer: guest.byCustomer ? Object.fromEntries(Object.entries(guest.byCustomer).map(([id, buckets]) => [id, fold(buckets)])) : guest.byCustomer,
    },
  };
}

/**
 * The counts contract from the per-product results. `byProduct` is the working rooftop's figures (the
 * hero and product cards). `breakdownByProduct` is the same modules run across every listed rooftop,
 * whose per-customer tallies build one row per rooftop for the Orders by rooftop cards (a rooftop with
 * no orders is present at zero); it is empty for a viewer who does not span rooftops.
 */
export function toHomeCounts(
  byProduct: Partial<Record<OrderType, ProductCounts>>,
  breakdownByProduct: Partial<Record<OrderType, ProductCounts>>,
  rooftopIds: number[],
): HomeCounts {
  const byRooftop: RooftopCounts[] = rooftopIds.map(id => {
    let orders = 0;
    let active = 0;
    let cancelled = 0;
    for (const counts of Object.values(breakdownByProduct)) {
      const buckets = counts?.byCustomer?.[id];
      if (!buckets) continue;
      orders += buckets.total;
      active += buckets.active;
      cancelled += buckets.cancelled;
    }
    return { id, orders, active, cancelled };
  });
  return { byProduct, byRooftop };
}
